import type { ItemClock } from '../model/v3-itinerary';
import {
  clockToMinutes,
  minutesToClock,
} from '@travel-planner/shared/utils/planner-day-clock';
import { Clock } from 'lucide-react';
import { useState } from 'react';
import { pickCss } from '@/shared/lib/css-utils';
import css from './planner-v3-itinerary.module.scss';

const pillCss = pickCss(css, css.timePill);
const editorCss = pickCss(css, css.timeEditor);

/** A clock whose end may be unset (a transfer's, T-425). */
type OpenClock = Omit<ItemClock, 'end'> & { end: string | null };

const CLOCK = /^\d{2}:\d{2}$/;

/** "09:30 – 11:30" on a day item (or "09:00" without an end); red when it overlaps another. */
export const ItemTimePill = ({
  title,
  clock,
  isOpen,
  onToggle,
}: {
  title: string;
  clock: OpenClock;
  isOpen: boolean;
  /** Absent: the time is shown, not edited. */
  onToggle?: () => void;
}) => {
  const overlap = clock.overlaps.length > 0;
  const label = clock.end ? `${clock.start} – ${clock.end}` : clock.start;
  const hint = overlap
    ? `Overlaps ${clock.overlaps.join(', ')}`
    : onToggle
      ? 'Change the time'
      : undefined;
  if (!onToggle) {
    return (
      <span className={pillCss({ isOverlap: overlap })} title={hint}>
        <Clock aria-hidden="true" size={11} />
        {label}
      </span>
    );
  }
  return (
    <button
      aria-expanded={isOpen}
      aria-label={`Time of ${title}: ${label}${overlap ? ` · ${hint}` : ''}`}
      className={pillCss({ isOverlap: overlap, isEditable: true })}
      onClick={onToggle}
      title={hint}
      type="button"
    >
      <Clock aria-hidden="true" size={11} />
      {label}
    </button>
  );
};

/**
 * Start and end of a day item, as XT's planner reschedules one. With
 * `endOptional` (a transfer) the end may stay empty; `onReset` brings back
 * its usual time.
 */
export const ItemTimeEditor = ({
  title,
  clock,
  onSave,
  onClose,
  endOptional = false,
  onReset,
}: {
  title: string;
  clock: OpenClock;
  /** `endTime` is empty only when `endOptional`. */
  onSave: (startTime: string, endTime: string) => void;
  onClose: () => void;
  endOptional?: boolean;
  onReset?: () => void;
}) => {
  const [start, setStart] = useState(clock.start);
  const [end, setEnd] = useState(clock.end ?? '');
  const lengthOf = (from: string, to: string) =>
    CLOCK.test(from) && CLOCK.test(to)
      ? clockToMinutes(to) - clockToMinutes(from)
      : 0;
  // The length a new start keeps: the one the agent last had, never one read
  // off a half-typed start (a time input commits each typed digit).
  const [length, setLength] = useState(() =>
    lengthOf(clock.start, clock.end ?? '')
  );
  const startValid = CLOCK.test(start);
  const noEnd = endOptional && end === '';
  const valid =
    startValid &&
    (noEnd || (CLOCK.test(end) && clockToMinutes(end) > clockToMinutes(start)));
  const error =
    endOptional && !startValid ? 'Set a start time' : 'End must be after start';
  // A new start keeps the item's length, as XT's reschedule does.
  const changeStart = (next: string) => {
    if (CLOCK.test(next) && length > 0) {
      const moved = clockToMinutes(next) + length;
      if (moved < 24 * 60) setEnd(minutesToClock(moved));
    }
    setStart(next);
  };
  const changeEnd = (next: string) => {
    setEnd(next);
    setLength(lengthOf(start, next));
  };
  return (
    <form
      aria-label={`Time of ${title}`}
      className={editorCss({ isFlat: endOptional })}
      onSubmit={(event) => {
        event.preventDefault();
        if (!valid) return;
        onSave(start, end);
        onClose();
      }}
    >
      <label className={css.timeField}>
        Start
        <input
          className={css.timeInput}
          onChange={(event) => changeStart(event.target.value)}
          required
          step={300}
          type="time"
          value={start}
        />
      </label>
      <label className={css.timeField}>
        {endOptional ? 'End (optional)' : 'End'}
        <input
          className={css.timeInput}
          onChange={(event) => changeEnd(event.target.value)}
          required={!endOptional}
          step={300}
          type="time"
          value={end}
        />
      </label>
      {valid ? null : (
        <span className={css.timeError} role="alert">
          {error}
        </span>
      )}
      <span className={css.commentActions}>
        {onReset ? (
          <button
            className={css.link}
            onClick={() => {
              onReset();
              onClose();
            }}
            type="button"
          >
            Usual time
          </button>
        ) : null}
        <button className={css.link} onClick={onClose} type="button">
          Cancel
        </button>
        <button className={css.commentSave} disabled={!valid} type="submit">
          Save
        </button>
      </span>
    </form>
  );
};
